/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, OnDestroy, OnInit} from '@angular/core';

import template from './political-sponsors-page.component.html';
import {PoliticalSponsorResource} from '../../../components/api/resources/resource/political-sponsor.resource';
import {createBufferedFlag, readBufferedFlag} from '../../common/util/template-state';
import {visiblePageNumbers} from '../../common/directives/pagination/visible-page-numbers';

const GROUP_BY_OPTIONS = [
  {value: 'SPONSOR', label: 'Sponsor'},
  {value: 'CONTROLLING', label: 'Controlling entity'},
  {value: 'PAYING', label: 'Payer'}
];

@Component({
  standalone: false,
  selector: 'adn-political-sponsors-page',
  template
})
export class PoliticalSponsorsPageComponent implements OnInit, OnDestroy {
  readonly groupByOptions = GROUP_BY_OPTIONS;

  groupBy = 'SPONSOR';
  loaded = true;
  private readonly loadedFlag = createBufferedFlag();
  private destroyed = false;
  searched = false;
  pageSize = 50;
  currentPage = 1;
  pageQuery: any = {$resolved: true};
  results: any[] = [];
  totalCount = 0;

  constructor(
    @Inject(PoliticalSponsorResource) private readonly PoliticalSponsor: any,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  get displayLoaded(): boolean {
    return readBufferedFlag(this.loadedFlag, this.loaded, this.cdr, () => !this.destroyed);
  }

  ngOnInit(): void {
    this.runQuery(true);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  groupByLabel(): string {
    const option = this.groupByOptions.find((candidate) => candidate.value === this.groupBy);
    return option ? option.label : this.groupBy;
  }

  declaredEntity(advertiser: any, type: 'CONTROLLING' | 'PAYING'): any {
    return (advertiser.entities || []).find((entity: any) => entity.type === type) || null;
  }

  runQuery(resetPage?: boolean): void {
    if (resetPage) {
      this.currentPage = 1;
    }
    this.loaded = false;
    this.pageQuery = this.PoliticalSponsor.summary({
      groupBy: this.groupBy,
      page: this.currentPage,
      pageSize: this.pageSize
    });
    this.pageQuery.$promise.then((data: any) => {
      this.results = data.results || [];
      this.totalCount = data.totalCount || 0;
      this.searched = true;
      this.loaded = true;
    }, () => {
      this.results = [];
      this.totalCount = 0;
      this.searched = true;
      this.loaded = true;
    });
  }

  changePage(page: number): void {
    if (page < 1 || page > this.totalPages() || page === this.currentPage) {
      return;
    }
    this.currentPage = page;
    this.runQuery(false);
  }

  totalPages(): number {
    return Math.max(1, Math.ceil((this.totalCount || 0) / this.pageSize));
  }

  pageNumbers(): number[] {
    return visiblePageNumbers(this.currentPage, this.totalPages());
  }

  pageRangeStart(): number {
    return this.pageSize * (this.currentPage - 1) + 1;
  }

  pageRangeEnd(): number {
    return Math.min(this.pageSize * this.currentPage, this.totalCount || 0);
  }
}
